/**
 * <cart-express-toggle>
 * Cart-drawer "Express processing" switch. Toggling ON adds the configured fee
 * variant to the cart; toggling OFF removes it.
 *
 * Important quirk this file works around: the theme refreshes the drawer via
 * the Section Rendering API (`sections=cart-drawer`), which renders the section
 * with DEFAULT schema settings — the section-group instance settings (where
 * express_enabled lives) are lost, so the server-rendered toggle disappears
 * from every re-render. We therefore stash the initial markup and re-inject it
 * into `.cart-drawer__bottom` after each re-render, then sync the checkbox
 * from the live cart.
 * 
 * Markup + settings: snippets/express-processing.liquid (data-variant-id).
 */
(() => {
  let stashedHTML = null; // canonical markup from the initial server render
  let observerArmed = false;

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function armReinjectObserver() {
    if (observerArmed) return;
    const body = document.getElementById('CartDrawer-Body');
    if (!body) return;
    observerArmed = true;

    new MutationObserver(() => {
      if (!stashedHTML) return;
      if (body.querySelector('cart-express-toggle')) return;
      const bottom = body.querySelector('.cart-drawer__bottom');
      if (!bottom) return; // empty cart — no footer, nothing to attach to
      bottom.insertAdjacentHTML('afterbegin', stashedHTML);
      // the re-inserted element's connectedCallback syncs its own state
    }).observe(body, { childList: true });
  }

  class CartExpressToggle extends HTMLElement {
    connectedCallback() {
      this.variantId = parseInt(this.getAttribute('data-variant-id') || '0', 10);
      this.checkbox = this.querySelector('[data-express-checkbox]');
      if (!this.checkbox || !this.variantId) return;

      if (!stashedHTML) stashedHTML = this.outerHTML;
      armReinjectObserver();

      this._onChange = this._onChange.bind(this);
      this.checkbox.addEventListener('change', this._onChange);

      this.syncState();
    }

    disconnectedCallback() {
      this.checkbox?.removeEventListener('change', this._onChange);
    }

    /** Set the switch from the real cart contents. */
    async syncState() {
      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        const inCart = cart.items.some((item) => item.variant_id === this.variantId);
        if (this.checkbox) this.checkbox.checked = inCart;
      } catch (e) {
        console.error('Express processing: cart sync failed', e);
      }
    }

    sectionsBody(cart) {
      const body = {};
      if (cart && typeof cart.getSectionsToRender === 'function') {
        const sections = cart.getSectionsToRender().map((s) => s.section);
        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
      }
      return body;
    }

    _onChange() {
      const add = this.checkbox.checked;
      const cart = getCartElement();
      this.classList.add('is-loading');
      this.checkbox.disabled = true;

      const url = add
        ? (window.routes && window.routes.cart_add_url) || '/cart/add.js'
        : (window.routes && window.routes.cart_change_url) || '/cart/change.js';

      // /cart/change accepts a variant id for `id`, so removal needs no line
      // key — but it must be a STRING (a numeric id gets a 400).
      const body = add
        ? { items: [{ id: this.variantId, quantity: 1 }], ...this.sectionsBody(cart) }
        : { id: String(this.variantId), quantity: 0, ...this.sectionsBody(cart) };

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((response) => {
          if (response.status) {
            // Add/remove failed (e.g. unavailable, not in cart) — revert the switch.
            console.error('Express processing error:', response.message || response.description);
            if (this.isConnected && this.checkbox) this.checkbox.checked = !add;
            return;
          }
          this._refresh(cart, response);
        })
        .catch((e) => {
          console.error(e);
          if (this.isConnected && this.checkbox) this.checkbox.checked = !add;
        })
        .finally(() => {
          this.classList.remove('is-loading');
          if (this.checkbox) this.checkbox.disabled = false;
        });
    }

    _refresh(cart, response) {
      try {
        if (cart && typeof cart.renderContents === 'function' && response && response.sections) {
          // Re-render the drawer without closing it. The MutationObserver
          // re-inserts this toggle and the new instance syncs its state.
          cart.renderContents(response);
          return;
        }
      } catch (e) {
        console.error('Express processing cart refresh failed:', e);
      }
      // No drawer API available — just resync the switch to the cart.
      this.syncState();
    }
  }

  if (!window.customElements.get('cart-express-toggle')) {
    window.customElements.define('cart-express-toggle', CartExpressToggle);
  }
})();
